/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ScrollView,
  TextInput,
  Dimensions,
  useWindowDimensions,
  TouchableOpacity,
} from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "../../../../store";
import {
  fetchPulseCheckData,
  PulseCheckMetric,
  updateMetrics,
  updateAttendance, // <-- ensure this is imported
  updateObservations,
  clearPulseCheckNotification,
} from "../../../../store/slices/nhoSlice";
import { resetTouchPointSurveyForm, resetFilters } from "../../../../store/slices/nhoSlice";
import {
  SignatureSection,
  PulseCheckTouchPointAction,
  TouchPointWelcomeCard,
} from "../../components/TouchPoint";
import DLOBreadcrumb, { DLOBreadcrumbItem } from "../../../../modules/dlo/components/DLOBreadcrumb";
import TouchPointPulseCheck from "../../components/TouchPointPulseCheck/TouchPointPulseCheck";
import TargetsDisplayTable from "../../components/TouchPointPulseCheck/TargetsDisplayTable";
import AttendanceTracking from "../../components/TouchPointPulseCheck/AttendanceTracking";
import { createApiClient, apiClient } from "../../../../api/apiClient";
import FontFamily from "../../../../constants/fonts";
import NhoErrorModal from "../../components/NhoErrorModal";
import { handleNHOErrorWithFriendlyMessage, NHO_API_NAMES } from '../../../../store/utils/nhoErrorHandler';
import { useTheme } from "../../../../theme";

const DateTimePicker =
  Platform.OS === "web"
    ? require("../../../../../web/DateTimePickerWebStub").default
    : require("@react-native-community/datetimepicker").default;

interface PulseCheckTouchPointProps {
  onBackClick?: () => void;
  onBackPress?: () => void; // Added this prop from App.tsx navigation
  onHomePress?: () => void; // Added for home navigation
}

const isIPad = Platform.OS === "ios" && Dimensions.get("window").width >= 768;
const isIPhone = Platform.OS === "ios" && Dimensions.get("window").width < 768;

const PulseCheckTouchPoint: React.FC<PulseCheckTouchPointProps> = ({
  onBackClick,
  onBackPress,
  onHomePress,
}) => {
  // Use onBackPress if provided (from App.tsx), otherwise use onBackClick (from internal navigation)
  const handleBackClick = onBackPress || onBackClick;
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const [employeeSignatureFileName, setEmployeeSignatureFileName] = useState<
    string | null
  >(null);
  const [employeeSignatureImageUrl, setEmployeeSignatureImageUrl] = useState<
    string | null
  >(null);
  const [managerSignatureFileName, setManagerSignatureFileName] = useState<
    string | null
  >(null);
  const [managerSignatureImageUrl, setManagerSignatureImageUrl] = useState<
    string | null
  >(null);

  // Handler to enable PDF export when employee name is clicked
  const [pdfExportEnabled, setPdfExportEnabled] = useState(false);
  const handleEmployeeNameClick = () => {
    setPdfExportEnabled(true);
  };

  // Redux hooks
  const dispatch = useDispatch<AppDispatch>();
  const { selectedLocation, periodWeek } = useSelector((state: any) => state.appData);

  // Redirect to NHO Dashboard when location changes while on this touchpoint page
  const initialLocationIdRef = useRef<string | undefined>(selectedLocation?.id);
  const handleBackClickRef = useRef(handleBackClick);
  useEffect(() => { handleBackClickRef.current = handleBackClick; }, [handleBackClick]);
  const hasRedirectedRef = useRef(false);
  useEffect(() => {
    const initialId = initialLocationIdRef.current;
    if (
      !hasRedirectedRef.current &&
      initialId &&
      selectedLocation?.id &&
      initialId !== selectedLocation.id
    ) {
      hasRedirectedRef.current = true;
      dispatch(resetFilters());
      if (handleBackClickRef.current) handleBackClickRef.current();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id]);

  const {
    pulseCheckData,
    pulseCheckLoading,
    pulseCheckError,
    observationNotes,
    pulseCheckNotification,
    touchPointSurvey,
  } = useSelector((state: any) => state.nho);
  const { user } = useSelector((state: any) => state.auth); // <-- get user from auth

  // Track if signatures have been initialized to prevent clearing them on form updates
  const [signaturesInitialized, setSignaturesInitialized] = useState(false);
  // Prevent duplicate submissions
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Get current period and week from Redux store
  const currentPeriod = periodWeek?.data?.period;
  const currentWeek = periodWeek?.data?.week;

  // Warning notification state
  const [showWarningNotification, setShowWarningNotification] = useState(false);

  // Defensive: Only run if pulseCheckData is defined and has a user object
  useEffect(() => {
    if (!pulseCheckData || !pulseCheckData.user) return;
    console.log("pulseCheckData full object:", pulseCheckData); // <--- LOG FULL OBJECT
    // Try to find status in multiple places
    const topLevelStatus = pulseCheckData.status;
    const userStatus = pulseCheckData.user.status;
    const altStatus = pulseCheckData.observations?.status;
    console.log("pulseCheckData.status:", topLevelStatus);
    console.log("pulseCheckData.user.status:", userStatus);
    console.log("pulseCheckData.observations?.status:", altStatus);
    
    const isCompleted = topLevelStatus === "completed" || userStatus === "completed" || altStatus === "completed";
    
    // Only initialize signatures once or when explicitly changing employees
    // Or reset signatures if current week needs an update
    if (!signaturesInitialized || needsCurrentWeekUpdate) {
      if (needsCurrentWeekUpdate) {
        console.log("Current week needs update - resetting signatures");
        setEmployeeSignatureFileName(null);
        setEmployeeSignatureImageUrl(null);
        setManagerSignatureFileName(null);
        setManagerSignatureImageUrl(null);
      } else {
        // Employee signature
        if (pulseCheckData.user.Employee_Signature) {
          setEmployeeSignatureFileName("Signed");
          setEmployeeSignatureImageUrl(pulseCheckData.user.Employee_Signature);
        } else {
          setEmployeeSignatureFileName(null);
          setEmployeeSignatureImageUrl(null);
        }
        
        // Manager signature
        if (pulseCheckData.user.Manager_Signature) {
          setManagerSignatureFileName("Signed");
          setManagerSignatureImageUrl(pulseCheckData.user.Manager_Signature);
        } else {
          setManagerSignatureFileName(null);
          setManagerSignatureImageUrl(null);
        }
      }
      
      setSignaturesInitialized(true);
    }
  }, [pulseCheckData, signaturesInitialized]);

  // PDF export should be enabled only if signatures exist and are from the API (pulseCheckData)
  useEffect(() => {
    // Check if signatures exist
    const hasSignatures = Boolean(employeeSignatureImageUrl && managerSignatureImageUrl);
    
    // Check if signatures are from pulseCheckData (API) and not locally captured
    const areSignaturesFromPulseCheckData = Boolean(
      pulseCheckData?.user?.Employee_Signature && 
      pulseCheckData?.user?.Manager_Signature && 
      pulseCheckData?.user?.Employee_Signature === employeeSignatureImageUrl && 
      pulseCheckData?.user?.Manager_Signature === managerSignatureImageUrl
    );
    
    // Enable PDF export only if signatures exist AND they are from pulseCheckData
    setPdfExportEnabled(hasSignatures && areSignaturesFromPulseCheckData);
  }, [employeeSignatureImageUrl, managerSignatureImageUrl, pulseCheckData]);

  // Handle warning notification display
  useEffect(() => {
    if (pulseCheckNotification) {
      setShowWarningNotification(true);
    }
  }, [pulseCheckNotification]);

  // Add state for employeeDetails
  const [employeeDetails, setEmployeeDetails] = React.useState({
    name: "",
    peerCoach: "",
    graduationDate: null as Date | null,
    weeksTillGraduation: null as number | null,
  });

  // Get selected employee data from nho slice
  const { selectedFilters } = useSelector((state: any) => state.nho);

  // Reset signatures and employee details when selected employee or location changes
  useEffect(() => {
    setEmployeeDetails({
      name: "",
      peerCoach: "",
      graduationDate: null,
      weeksTillGraduation: null,
    });
    
    // Reset signature initialization flag so they can be reloaded for the new employee
    setSignaturesInitialized(false);
    setEmployeeSignatureFileName(null);
    setEmployeeSignatureImageUrl(null);
    setManagerSignatureFileName(null);
    setManagerSignatureImageUrl(null);
  }, [selectedFilters, selectedLocation]);

  // Fetch pulse check data when component mounts or when employee selection changes
  useEffect(() => {
    const fetchPulseCheckInfo = async () => {
      // Get GPID and location from Redux store or from nhodashboardtable
      const gpid = selectedFilters?.employeeGpid;
      const location = selectedLocation?.displayText || selectedLocation?.name;

      if (gpid && location) {
        try {
          console.log("Fetching pulse check data for:", { gpid, location });
          const result = await dispatch(
            fetchPulseCheckData({ gpid, location })
          );

          if (fetchPulseCheckData.fulfilled.match(result)) {
            // Update employee details with the fetched data
            const userData = result.payload.user;
            setEmployeeDetails({
              name: `${userData.First_Name} ${userData.Last_Name}`,
              peerCoach: userData.Peer_Coach || "Not assigned",
              graduationDate: userData.Expected_Graduation_Date
                ? new Date(userData.Expected_Graduation_Date)
                : null,
              weeksTillGraduation: userData.Weeks_till_graduation,
            });
            
            // observationNotes will be automatically synced by Redux slice
            console.log(
              "Pulse check data loaded successfully:",
              result.payload
            );
            console.log("User data:", result.payload.user);
            console.log("Metrics data:", result.payload.metrics);
            console.log(
              "Raw metrics data length:",
              result.payload.metrics?.length || 0
            );
            console.log("Attendance data:", result.payload.attendance);
          } else if (fetchPulseCheckData.rejected.match(result)) {
            console.error("Failed to fetch pulse check data:", result.payload);
          }
        } catch (error) {
          console.error("Error fetching pulse check data:", error);
        }
      } else {
        console.log("Missing required data for pulse check API call:", {
          gpid: gpid || "Not found",
          location: location || "Not found",
          selectedFilters,
          selectedLocation,
        });

        // Clear any existing pulse check data if we don't have the required params
        if (!gpid || !location) {
          setEmployeeDetails({
            name: "",
            peerCoach: "",
            graduationDate: null,
            weeksTillGraduation: null,
          });
        }
      }
    };

    fetchPulseCheckInfo();
  }, [dispatch, selectedLocation, selectedFilters]);

  // Get window dimensions for responsive design
  const { width } = useWindowDimensions();
  // Function to determine if we're in mobile view
  const isMobileView = width < 768;

  // Local helper to return the title used in other touchpoints
  const getWelcomeTitle = () => "TouchPoint-Pulse Check";

  // Handler for updating employeeDetails
  const handleEmployeeDetailChange = (
    field: string | number | symbol,
    value: any
  ) => {
    setEmployeeDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  // Removing unused table data and columns since we're using TargetsDisplayTable now

  const [rows, setRows] = React.useState([
    { id: 0, date: new Date(), input: "" },
  ]);
  const [showDatePicker, setShowDatePicker] = React.useState<{
    [key: number]: boolean;
  }>({});

  const handleInputChange = (id: number, value: string) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, input: value } : row))
    );
  };

  const handleDateChange = (id: number, event: any, selectedDate?: Date) => {
    setShowDatePicker((prev) => ({ ...prev, [id]: false }));
    if (selectedDate) {
      setRows((prev) =>
        prev.map((row) =>
          row.id === id ? { ...row, date: selectedDate } : row
        )
      );
    }
  };

  const handleAddRow = () => {
    setRows((prev) => [
      ...prev,
      {
        id: prev.length > 0 ? prev[prev.length - 1].id + 1 : 0,
        date: new Date(),
        input: "",
      },
    ]);
  };

  const handleEmployeeSignature = (fileName: string | null, imageUrl?: string | null) => {
    console.log("Employee signature captured:", fileName, imageUrl);
    setEmployeeSignatureFileName(fileName ?? '');
    if (imageUrl) {
      // Always store the full URL for the signature
      setEmployeeSignatureImageUrl(imageUrl);
    }
  };

  // Handle manager signature capture
  const handleManagerSignature = (fileName: string | null, imageUrl?: string | null) => {
    console.log("Manager signature captured:", fileName, imageUrl);
    setManagerSignatureFileName(fileName ?? '');
    if (imageUrl) {
      // Always store the full URL for the signature
      setManagerSignatureImageUrl(imageUrl);
    }
  };

  // --- Force disable logic for action buttons and read-only mode ---
  // Check for a completed status using a fallback: if both signatures exist, treat as completed
  const hasSignatures = Boolean(pulseCheckData?.user?.Employee_Signature && pulseCheckData?.user?.Manager_Signature);
  
  // Check if warning notification should make form read-only (only if Weeks_till_graduation is negative)
  const shouldBeReadOnlyFromWarning = showWarningNotification && 
    pulseCheckData?.user?.Weeks_till_graduation != null && 
    pulseCheckData.user.Weeks_till_graduation < 0;
  
  // Find the current week's metric based on current period and pepsi week
  const currentWeekMetric = pulseCheckData?.metrics?.find(
    (metric: PulseCheckMetric) => 
      metric.Period === currentPeriod && 
      metric.Pepsi_Week === currentWeek
  );
  
  // Check if current week's metrics need updating (is_updated = false)
  const needsCurrentWeekUpdate = currentWeekMetric && 
    currentWeekMetric.is_actual && 
    currentWeekMetric.is_updated === false;
  // If the current week needs an update, we should allow editing regardless of signature status
  const isPulseCheckCompleted = (hasSignatures || shouldBeReadOnlyFromWarning) && !needsCurrentWeekUpdate;
  
  // PDF export should be enabled ONLY if completed
  // Only enable PDF export if both signatures exist AND no warning-based read-only mode
  const pdfExportEnabledFinal = hasSignatures && !shouldBeReadOnlyFromWarning;
  const forceDisabled = !isPulseCheckCompleted;

  // Pass read-only mode to child components
  const renderMainContent = () => {
    // Log observationNotes value before rendering TextInput
    
    // Find the current week's metric based on current period and pepsi week
    const currentWeekMetric = pulseCheckData?.metrics?.find(
      (metric: PulseCheckMetric) => 
        metric.Period === currentPeriod && 
        metric.Pepsi_Week === currentWeek
    );
    
    return (
      <ScrollView style={styles.contentScrollView}>
        <View style={styles.headerContainer}>
          <DLOBreadcrumb
            items={[
              {
                id: 'nho',
                label: 'NHO',
                onPress: () => {
                  // reset touchpoint form state and navigate back
                  dispatch(resetTouchPointSurveyForm());
                  if (handleBackClick) handleBackClick();
                },
              },
              {
                id: 'touchpoint',
                label: getWelcomeTitle(),
                isCurrentPage: true,
              },
            ]}
            showHomeIcon={true}
            onHomePress={onHomePress}
          />

          <View style={styles.titleContainer}>
            <Text style={styles.title}>{getWelcomeTitle()}</Text>
          </View>
        </View>
        <View
          style={[
            styles.contentArea,
            isDarkTheme && {
              backgroundColor: colors.background.surface,
              borderWidth: 1,
              borderColor: colors.border.default,
            },
          ]}
        >
          <TouchPointPulseCheck
            employeeDetails={employeeDetails}
            handleEmployeeDetailChange={handleEmployeeDetailChange}
            styles={styles}
            isViewOnlyMode={isPulseCheckCompleted} // Restore, required by TouchPointPulseCheckProps
          />
          {/*table*/}
          <View
            style={[
              styles.tableContainer,
              isMobileView && styles.tableContainerMobile,
            ]}
          >
            <TargetsDisplayTable
              testID="employee-targets-display"
              metricsData={
                pulseCheckData?.metrics?.map((metric: PulseCheckMetric) => {
                  // Extract the week number from the Week field (e.g., "Week1" -> 1)
                  const weekMatch = String(metric.Week || "").match(/Week(\d+)/);
                  const weekNum = weekMatch ? parseInt(weekMatch[1], 10) : 0;
                  
                  // Calculate if this week is in the past (read-only) based on graduation timing
                  const weeksTillGraduationFromHireDate = pulseCheckData?.user?.Weeks_till_graduation_from_Hire_Date || 0;
                  const weeksTillGraduation = pulseCheckData?.user?.Weeks_till_graduation || 0;
                  
                  // Check if the user has graduated (Weeks_till_graduation is 0 or negative)
                  const hasGraduated = weeksTillGraduation <= 0;
                  
                  // Calculate how many weeks have passed since hiring
                  const weeksPassed = weeksTillGraduationFromHireDate - weeksTillGraduation;
                  const currentWeekUpdated = pulseCheckData.metrics.some((m: PulseCheckMetric) => m.is_updated === true)
                  
                  // This row should be read-only if:
                  // 1. The user has already graduated (make ALL rows read-only) OR
                  // 2. This week is in the past (its number is <= weeks passed since hiring) OR
                  // 3. The metric is already updated (is_updated = true)
                  const isWeekInPast = hasGraduated || weekNum <= weeksPassed || metric.is_updated === true;
                                                      
                  return {
                    Week: String(metric.Week || 0),
                    DPS_Goal: metric.DPS_Target || 0,
                    DPS_Actual:
                      metric.DPS_Actual !== null ? String(metric.DPS_Actual) : "",
                    CPH_Goal: metric.CPH_Target || 0,
                    CPH_Actual:
                      metric.CPH_Actual !== null ? String(metric.CPH_Actual) : "",
                    Gap_Time_Goal: metric.Gap_Time_Target || 0,
                    Gap_Time_Actual:
                      metric.Gap_Time_Actual !== null
                        ? String(metric.Gap_Time_Actual)
                        : "",
                    SIA_Goal: metric.SIA_Target || 0,
                    SIA_Actual:
                      metric.SIA_Actual !== null ? String(metric.SIA_Actual) : "",
                    period: metric.Period ? String(metric.Period) : "",
                    week: metric.Pepsi_Week ? String(metric.Pepsi_Week) : "",
                    originalMetric: metric,
                    // A row is read-only if: it's from a past week, user has graduated, or is already updated
                    isReadOnly: isWeekInPast,
                  };
                }) || []
              }
              onMetricUpdate={(updatedMetrics) => {
                // Convert and validate metrics before updating
                const convertedMetrics = updatedMetrics
                  .map((metric) => {
                    const converted = {
                      ...metric.originalMetric,
                      DPS_Actual: metric.DPS_Actual
                        ? parseFloat(metric.DPS_Actual)
                        : null,
                      CPH_Actual: metric.CPH_Actual
                        ? parseFloat(metric.CPH_Actual)
                        : null,
                      Gap_Time_Actual: metric.Gap_Time_Actual
                        ? parseFloat(metric.Gap_Time_Actual)
                        : null,
                      SIA_Actual: metric.SIA_Actual
                        ? parseFloat(metric.SIA_Actual)
                        : null,
                      Period: metric.period
                        ? parseInt(metric.period, 10)
                        : null,
                      Pepsi_Week: metric.week
                        ? parseInt(metric.week, 10)
                        : null,
                    };

                    // Return only if there are actual changes
                    return Object.keys(converted).some(
                      (key) => converted[key] !== metric.originalMetric[key]
                    )
                      ? converted
                      : metric.originalMetric;
                  })
                  .filter(Boolean);

                // Only dispatch if there are actual changes
                if (
                  convertedMetrics.length > 0 &&
                  JSON.stringify(convertedMetrics) !==
                    JSON.stringify(pulseCheckData?.metrics)
                ) {
                  dispatch(updateMetrics(convertedMetrics));
                }
              }}
              onEmployeeNameClick={handleEmployeeNameClick}
            />
          </View>

          {/* Attendance and Notes Sections */}
          <View
            style={[
              styles.attendanceNotesRow,
              isMobileView && styles.attendanceNotesRowMobile,
            ]}
          >
            {/* Attendance Tracking Section */}
            <View
              style={[
                styles.attendanceTrackingOuterContainer,
                isMobileView && styles.attendanceTrackingOuterContainerMobile,
              ]}
            >
              <View
                style={[
                  styles.attendanceTrackingHeaderContainer,
                  isMobileView &&
                    styles.attendanceTrackingHeaderContainerMobile,
                ]}
              >
                <Text style={styles.attendanceTrackingHeader}>
                  Attendance Tolerance
                </Text>
              </View>
              <View
                style={[
                  styles.attendanceTrackingContainer,
                  isMobileView && styles.attendanceTrackingContainerMobile,
                  isDarkTheme && {
                    backgroundColor: colors.background.surface,
                    borderColor: colors.border.default,
                  },
                ]}
              >
                <AttendanceTracking
                  styles={styles}
                  attendanceData={pulseCheckData?.attendance}
                  onAttendanceChange={(updatedAttendance) => {
                    // The updateAttendance action expects attendance array with Absences, Date_of_Absence, Points
                    // If your AttendanceTracking only provides Date_of_Absence and Points, add Absences: 0
                    const attendanceWithAbsences = updatedAttendance.map(
                      (a) => ({ ...a })
                    );
                    dispatch(updateAttendance(attendanceWithAbsences));
                  }}
                  isViewOnlyMode={isPulseCheckCompleted}
                />
              </View>
            </View>

            {/* Observation Notes Section */}
            <View
              style={[
                styles.observationNotesOuterContainer,
                isMobileView && styles.observationNotesOuterContainerMobile,
              ]}
            >
              <View
                style={[
                  styles.observationNotesHeaderContainer,
                  isMobileView && styles.observationNotesHeaderContainerMobile,
                ]}
              >
                <Text style={styles.observationNotesHeader}>
                  Observation Notes
                </Text>
              </View>
              <View
                style={[
                  styles.observationNotesContainer,
                  isMobileView && styles.observationNotesContainerMobile,
                  isDarkTheme && {
                    backgroundColor: colors.background.surface,
                    borderColor: colors.border.default,
                  },
                ]}
              >
                <TextInput
                  style={[
                    styles.observationNotesInput,
                    isDarkTheme && {
                      backgroundColor: colors.background.elevated,
                      borderColor: colors.border.default,
                      color: colors.text.primary,
                    },
                  ]}
                  placeholder="Enter your observation notes here..."
                  placeholderTextColor={
                    isDarkTheme ? colors.text.secondary : "#FFFFFF"
                  }
                  value={observationNotes || ""}
                  onChangeText={(text) => {
                    dispatch(updateObservations(text));
                  }}
                  multiline
                  numberOfLines={isMobileView ? 6 : 4}
                  textAlignVertical="top"
                  editable={!isPulseCheckCompleted}
                />
              </View>
            </View>
          </View>

          {/* Signature Section */}
          <View
            style={[
              styles.signatureSectionContainer,
              isMobileView && styles.signatureSectionContainerMobile,
            ]}
          >
            <SignatureSection
              employeeSignatureFileName={employeeSignatureFileName}
              employeeSignatureImageUrl={employeeSignatureImageUrl}
              managerSignatureFileName={managerSignatureFileName}
              managerSignatureImageUrl={managerSignatureImageUrl}
              handleEmployeeSignature={handleEmployeeSignature}
              handleManagerSignature={handleManagerSignature}
              isViewOnlyMode={isPulseCheckCompleted}
            />
          </View>

          {/* Action Buttons */}
          <View style={styles.actionContainer}>
            <PulseCheckTouchPointAction
              onComplete={async () => {
                // Add same validation logic but make function async and await the API call
                if (!employeeSignatureImageUrl || !managerSignatureImageUrl) {
                  alert("Both employee and manager signatures are required.");
                  return false;
                }

                if (
                  !pulseCheckData?.metrics ||
                  pulseCheckData.metrics.length === 0
                ) {
                  alert("No metrics data available.");
                  return false;
                }

                const gpid = selectedFilters?.employeeGpid;
                const location =
                  selectedLocation?.displayText || selectedLocation?.name;
                const employeeName =
                  [selectedFilters?.employeeFirstName, selectedFilters?.employeeLastName]
                    .filter(Boolean)
                    .join(" ") || employeeDetails.name || "";

                const getDisplayNameFromEmail = (email: string): string => {
                  if (!email) {
                    return "";
                  }
                  const prefix = email.split("@")[0];
                  const parts = prefix.split(".");
                  if (parts.length === 1) {
                    return capitalize(parts[0]);
                  }
                  return `${capitalize(parts[0])} ${capitalize(parts[1])}`;
                };
                const capitalize = (s: string) =>
                  s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

                const formatAttendanceForSubmission = (attendance: any[]) => {
                  return attendance.map((item) => ({
                    ...item,
                    Points:
                      typeof item.Points === "number"
                        ? Number(parseFloat(item.Points.toString()).toFixed(2))
                        : 0,
                  }));
                };

                const formattedAttendance = formatAttendanceForSubmission(
                  pulseCheckData.attendance || []
                );

                const invalidAttendanceItem = formattedAttendance.find(
                  (item) =>
                    typeof item.Points !== "number" ||
                    isNaN(item.Points) ||
                    item.Points < 0
                );

                if (invalidAttendanceItem) {
                  console.error(
                    "PulseCheckTouchPoint: Invalid attendance data found:",
                    invalidAttendanceItem
                  );
                  alert(
                    "Invalid attendance data detected. Please check your attendance entries and ensure all values are valid numbers."
                  );
                  return false;
                }

                const createdByDisplayName = user?.username
                const submissionData = {
                  GPID: gpid || "",
                  Location_Name: location || "",
                  Employee_Name: employeeName,
                  createdBy: createdByDisplayName,
                  metrics: pulseCheckData.metrics.map(
                    (metric: PulseCheckMetric) => ({
                      Id: metric.Id,
                      DPS_Target: metric.DPS_Target || 0,
                      CPH_Target: metric.CPH_Target || 0,
                      Gap_Time_Target: metric.Gap_Time_Target || 0,
                      SIA_Target: metric.SIA_Target || 0,
                      Attendance: metric.Attendance || 0,
                      Week: metric.Week,
                      DPS_Actual: metric.DPS_Actual || 0,
                      CPH_Actual: metric.CPH_Actual || 0,
                      Gap_Time_Actual: metric.Gap_Time_Actual || 0,
                      SIA_Actual: metric.SIA_Actual || 0,
                      Period: metric.Period || 0,
                      Pepsi_Week: metric.Pepsi_Week || 0,
                      is_actual: metric.is_actual || false,
                    })
                  ),
                  attendance: formattedAttendance, // Use formatted attendance data
                  observations: observationNotes, // Pass edited observation notes
                  Comments1: observationNotes, // Pass edited observation notes
                  manager_signature: managerSignatureImageUrl || "",
                  employee_signature: employeeSignatureImageUrl || "",
                  status: "Completed",
                };

                // Prevent duplicate submissions
                if (isSubmitting) {
                  console.warn('PulseCheck submission blocked: already submitting');
                  return false;
                }

                setIsSubmitting(true);
                try {
                  const api = createApiClient();
                  const response = await apiClient(
                    api,
                    `new-hire-onboarding/pulse-check?gpid=${gpid}&location=${encodeURIComponent(
                      location
                    )}`,
                    "POST",
                    submissionData
                  );

                  // apiClient returns response.data; treat any truthy response as success
                  if (response) {
                    // Refresh data after success
                    dispatch(fetchPulseCheckData({ gpid, location }));
                    return true;
                  } else {
                    const userFacingError = 'Unable to submit pulse check. Please try again.';
                    try {
                      handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.SUBMIT_PULSE_CHECK, userFacingError, true, { submissionData, gpid, location });
                    } catch (e) {
                      console.error('Error while dispatching NHO centralized handler for submitPulseCheck (no-response):', e);
                    }
                    return false;
                  }
                } catch (error) {
                  console.error("Error submitting pulse check:", error);
                  try {
                    handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.SUBMIT_PULSE_CHECK, error, true, { submissionData, gpid, location });
                  } catch (e) {
                    console.error('Error while dispatching NHO centralized handler for submitPulseCheck:', e);
                  }
                  return false;
                } finally {
                  setIsSubmitting(false);
                }
              }}
              onSaveForLater={() => {
                alert("Progress saved successfully. You can continue later.");
              }}
              onBackToDashboard={handleBackClick}
              selectedTouchPoint={{
                type: "pulse_check",
                employeeSignature: employeeSignatureFileName,
                managerSignature: managerSignatureFileName,
              }}
              isFormComplete={Boolean(employeeSignatureImageUrl && managerSignatureImageUrl)}
              pdfExportEnabled={pdfExportEnabled}
              forceDisabled={isPulseCheckCompleted} // Only disable if loaded as completed from table
              isViewOnlyMode={isPulseCheckCompleted}
            />
            {/* DEBUG LOGS: See console for prop values passed to PulseCheckTouchPointAction */}
          </View>
        </View>
      </ScrollView>
    );
  };

  const renderErrorState = () => (
    <>
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>{pulseCheckError}</Text>
      </View>
      <TouchPointWelcomeCard
        title="TouchPoint-Pulse Check"
        onBackClick={handleBackClick}
        testID="touchpoint-welcome-card"
      />
    </>
  );

  return (
    <View
      style={[
        styles.container,
        isDarkTheme && { backgroundColor: colors.background.page },
      ]}
      testID="pulse-check-touchpoint"
    >
      {pulseCheckLoading && (
        <View
          style={[
            styles.loadingContainer,
            isDarkTheme && {
              backgroundColor: colors.background.surface,
              borderColor: colors.border.default,
              borderWidth: 1,
            },
          ]}
        >
          <Text
            style={[
              styles.loadingText,
              isDarkTheme && { color: colors.text.primary },
            ]}
          >
            Loading pulse check data...
          </Text>
        </View>
      )}

      {showWarningNotification && pulseCheckNotification && (
        <View
          style={[
            styles.warningContainer,
            isDarkTheme && {
              backgroundColor: colors.statusBackground.warning,
              borderColor: colors.status.warning,
            },
          ]}
        >
          <View style={styles.warningContent}>
            <Text
              style={[
                styles.warningText,
                isDarkTheme && { color: colors.text.primary },
              ]}
            >
              {pulseCheckNotification.message}
            </Text>
            <TouchableOpacity
              style={[
                styles.closeButton,
                isDarkTheme && {
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  borderWidth: 1,
                  borderColor: colors.text.primary,
                },
              ]}
              onPress={() => {
                setShowWarningNotification(false);
                dispatch(clearPulseCheckNotification());
              }}
              testID="close-warning-notification"
            >
              <Text
                style={[
                  styles.closeButtonText,
                  isDarkTheme && { color: colors.text.primary },
                ]}
              >
                ×
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {!pulseCheckLoading &&
        (pulseCheckError ? renderErrorState() : renderMainContent())}

      {/* Ensure NHO centralized error modal is mounted for this touchpoint */}
      <NhoErrorModal />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F5F9FF",
    padding: Platform.OS === "web" ? 26 : 16,
    width: "100%",
  },
  welcomeCardContainer: {
    backgroundColor: "white",
    borderRadius: 8,
    marginTop: 16,
    padding: Platform.OS === "web" ? 16 : 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  tableContainer: {
    marginBottom: 20,
    width: "100%",
    overflow: "hidden",
  },
  tableContainerMobile: {
    marginBottom: 12,
    width: "100%",
    paddingHorizontal: 0,
    overflow: "hidden",
  },
  contentScrollView: {
    flex: 1,
  },
  contentArea: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: Platform.OS === "web" ? 14 : 10, // Reduced padding to provide more space
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    width: "100%",
  },
  contentText: {
    fontSize: Platform.OS === "web" ? 16 : 14,
    color: "#2D2D2D",
  },
  formInputsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Platform.OS === "web" ? 16 : 8,
    justifyContent: "space-between",
    padding: Platform.OS === "web" ? 15 : 10,
    borderRadius: 8,
  },
  inputGroupsRow: {
    flexDirection: isIPhone ? "column" : "row",
    flexWrap: isIPhone ? "nowrap" : "wrap",
    justifyContent: "space-between",
    gap: Platform.OS === "web" ? 16 : 8,
  },
  inputGroup: {
    flex: isIPhone ? 0 : 1,
    width: isIPhone ? "100%" : undefined,
    minWidth: isIPhone ? "100%" : Platform.OS === "web" ? 250 : 150,
    maxWidth: isIPhone ? "100%" : Platform.OS === "web" ? 300 : "100%",
    marginBottom: 16,
    marginHorizontal: Platform.OS === "web" ? 8 : 0,
  },
  labelColumn: {
    //minHeight: 40, 
    height: 18,   
    justifyContent: "flex-start",
    alignItems: "flex-start",
    marginBottom: 2,
    flexDirection: "row",
    width: "100%",
  },
  formLabel: {
    //backgroundColor: "#0066cc",
    fontSize: Platform.OS === "web" ? 12 : 12,
    fontWeight: "400",
    color: "#2D2D2D",
    textAlign: "left",
    width: "100%",
  },
  inputColumn: {
    width: "100%",
  },
  formInput: {
    fontSize: Platform.OS === "web" ? 15 : 14,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: "white",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#ced4da",
    width: "100%",
    height: 44,
  },
  actionButton: {
    backgroundColor: "green",
    width:
      Dimensions.get("window").width < 400
        ? 90
        : Platform.OS === "web"
        ? "12%"
        : 120,
    minWidth: Platform.OS === "web" ? 90 : 60,
    fontSize: Dimensions.get("window").width < 400 ? 12 : 16,
    marginTop: 15,
    marginLeft: 10,
  },
  disabledButton: {
    backgroundColor: "#8FBC8F",
    borderColor: "white",
    width:
      Dimensions.get("window").width < 400
        ? 90
        : Platform.OS === "web"
        ? "12%"
        : 120,
    minWidth: Platform.OS === "web" ? 90 : 60,
    fontSize: Dimensions.get("window").width < 400 ? 12 : 16,
    marginTop: 15,
    marginLeft: 10,
    opacity: 0.7,
  },
  actionContainer: {
    //marginTop: 20,
    alignItems: Platform.OS === "web" && isIPad ? "flex-start" : "center",
    width: Platform.OS === "web" && isIPad ? "40%" : "100%",
  },
  // Standard desktop row layout
  attendanceNotesHeaderRow: {
    flexDirection: "row",
    alignItems: "stretch",
    marginTop: 24,
    marginBottom: 0,
    gap: Platform.OS === "web" ? 16 : 8,
    width: "100%",
  },
  // Mobile stacked column layout
  attendanceNotesHeaderRowMobile: {
    flexDirection: "column",
    gap: 8,
    width: "100%",
  },
  // Standard desktop row layout
  attendanceNotesRow: {
    flexDirection: "row",
    alignItems: "flex-start", // Changed from 'stretch' to 'flex-start' to prevent stretching
    marginBottom: 16,
    gap: Platform.OS === "web" ? 4 : 8,
    width: "100%",
  },
  // Mobile stacked column layout
  attendanceNotesRowMobile: {
    flexDirection: "column",
    gap: 12,
    width: "100%",
    alignItems: "stretch",
  },
  attendanceTrackingContainer: {
    flex: 1,
    minWidth: 0,
    marginRight: 0,
    alignSelf: "stretch",
    width: "100%",
    overflow: "visible", // Changed to 'visible' to allow calendar overlay
    minHeight: 150,
    maxHeight: 300, // Added max height to prevent excessive growth
    borderWidth: 0,
    borderColor: "transparent",
  },
  attendanceTrackingContainerMobile: {
    marginRight: 0,
    marginBottom: 16,
    width: "100%",
  },
  observationNotesContainer: {
    flex: 1,
    minWidth: 0,
    marginLeft: 0,
    maxWidth: "100%",
    alignSelf: "stretch",
    display: "flex",
    width: "100%",
    padding:40,
    backgroundColor:"white",
    borderWidth:1,
    borderColor:"#DADADA",
  },
  observationNotesContainerMobile: {
    marginLeft: 0,
    width: "100%",
  },
  attendanceTrackingHeaderContainer: {
    backgroundColor: "#034895",
    // borderTopLeftRadius: 6,
    // borderTopRightRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 16,
    // marginRight: 8,
    justifyContent: "center",
    width: "100%",
    height: 60, // Fixed height for the header
  },
  attendanceTrackingHeaderContainerMobile: {
    marginRight: 0,
    width: "100%",
  },
  attendanceTrackingHeader: {
    color: "white",
    fontSize: Platform.OS === "web" ? 16 : 14, // Reduced from 18/16
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 0,
  },
  observationNotesHeaderContainer: {
    backgroundColor: "#034895",
    // borderTopLeftRadius: 6,
    // borderTopRightRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 16,
    // marginLeft: 8,
    justifyContent: "center",
    width: "100%",
    height: 60, // Fixed height for the header
  },
  observationNotesHeaderContainerMobile: {
    marginLeft: 0,
    width: "100%",
  },
  observationNotesHeader: {
    color: "white",
    fontSize: Platform.OS === "web" ? 16 : 14, // Reduced from 18/16
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 0,
  },
  observationNotesInput: {
    borderWidth: 1,
    borderColor: "#0066cc",
    color: "#2D2D2D",
    borderRadius: 2,
    padding: 12,
    fontSize: Platform.OS === "web" ? 16 : 14,
    minHeight: 150, // Increased from 120 to ensure more space for notes
    marginBottom: 10, // Reduced from 20 to compensate for the increased minHeight
    flex: 1,
    width: "100%",
  },
  signatureSectionContainer: {
    width: "100%",
    marginTop: 16,
    marginBottom: 24,
  },
  signatureSectionContainerMobile: {
    marginTop: 12,
    marginBottom: 20,
  },
  attendanceTrackingOuterContainer: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    marginRight: 0,
    width: "100%",
    height: "auto", // Allow container to adjust height based on content
    maxWidth: "100%",
  },
  attendanceTrackingOuterContainerMobile: {
    marginRight: 0,
    marginBottom: 16,
    width: "100%",
  },
  observationNotesOuterContainer: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    marginLeft: 0,
    width: "100%",
    height: "auto", // Allow container to adjust height based on content
    maxWidth: "100%",
  },
  observationNotesOuterContainerMobile: {
    marginLeft: 0,
    width: "100%",
  },
  loadingContainer: {
    padding: 20,
    alignItems: "center",
    backgroundColor: "#f8f9fa",
    margin: 16,
    borderRadius: 8,
  },
  loadingText: {
    fontSize: 16,
    color: "#616161",
  },
  errorContainer: {
    padding: 20,
    alignItems: "center",
    backgroundColor: "#f8d7da",
    margin: 0,
    marginTop: 16,
    borderRadius: 8,
    borderColor: "#f5c6cb",
    borderWidth: 1,
  },
  errorText: {
    fontSize: 16,
    color: "#721c24",
  },
  warningContainer: {
    padding: 20,
    alignItems: "center",
    backgroundColor: "#fff3cd",
    margin: 0,
    marginTop: 16,
    borderRadius: 8,
    borderColor: "#ffeaa7",
    borderWidth: 1,
  },
  warningContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  warningText: {
    fontSize: 16,
    color: "#856404",
    textAlign: "center",
    flex: 1,
  },
  closeButton: {
    marginLeft: 16,
    padding: 4,
    borderRadius: 4,
    backgroundColor: "rgba(133, 100, 4, 0.1)",
    minWidth: 30,
    minHeight: 30,
    alignItems: "center",
    justifyContent: "center",
  },
  closeButtonText: {
    fontSize: 20,
    color: "#856404",
    fontWeight: "bold",
    lineHeight: 20,
  },
  headerContainer: {
    width: "100%",
    marginBottom: 8,
  },
  titleContainer: {
    marginTop: 14,
    marginBottom: 14,
  },
  title: {
    fontSize: Platform.OS === "web" ? 28 : (isIPad ? 24 : 18),
    fontWeight: "700",
    color: "#2D2D2D",
    fontFamily:FontFamily.bold

  },
});

export default PulseCheckTouchPoint;
